import type { Content } from 'pdfmake/interfaces';

import type { IAgency } from '@/models/agency';
// Import from leaf modules (not the `@/utils` barrel) to avoid a circular import
// if this helper is ever re-exported from `@/utils`.
import { formatAgencyAddress } from '@/utils/common';
import { formatPhoneNumber } from '@/utils/pattern';

type Margin = [number, number, number, number];

interface BuildReportHeaderParams {
  agency: IAgency | null | undefined;
  /** Pre-resolved base64 logo (already fetched by the caller), or undefined. */
  logo: string | undefined;
  /**
   * Report-specific column (e.g. "ARREST REPORT", Case ID, Printed on/by).
   * Build it fresh inside the caller's per-page `header()` closure — see the
   * per-page freshness note below.
   */
  reportEntries: Content[];
  /** Outer table margin. Default [20, 10, 20, 10]; Kase + Person use [20, 10, 20, 30]. */
  outerMargin?: Margin;
  /** Inner (bordered box) padding. Default { h: 12, v: 8 }; CFS uses { h: 3, v: 2 }. */
  innerLayoutPadding?: { h: number; v: number };
}

/**
 * Shared agency letterhead for the printed PDF reports (Arrest, CFS, Kase, Person).
 * Renders the agency logo, the agency name + contact block, and a report-specific column
 * inside a bordered grey box, positioned by `agency.headerAlignment`
 * (`'left'` | `'right'` | `'center'` — where the UI label "Both" maps to `'center'`).
 *
 * pdfMake mutates the node objects it lays out and invokes the page `header()` once PER
 * PAGE, so this rebuilds its whole node graph on every call. Callers must likewise build a
 * fresh `reportEntries` on every `header()` call (do not hoist it out of the closure).
 */
export const buildReportHeader = ({
  agency,
  logo,
  reportEntries,
  outerMargin = [20, 10, 20, 10],
  innerLayoutPadding = { h: 12, v: 8 },
}: BuildReportHeaderParams): Content => {
  const alignment = agency?.headerAlignment || 'left';

  // Build a fresh logo column each time (pdfMake mutates objects, so shared refs break).
  const buildLogoColumn = (): Content[] => {
    if (!logo) return [];
    return [
      {
        table: {
          widths: ['auto'],
          body: [[{ image: logo, width: 50 }]],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#000',
          vLineColor: () => '#000',
          paddingLeft: () => 5,
          paddingRight: () => 5,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
        margin: [0, 0, 0, 0] as Margin,
      },
    ];
  };

  const agencyInfo: Content[] = [
    {
      text: agency?.name || '',
      style: 'headerAgencyName',
      margin: [0, 0, 0, 4] as Margin,
    },
  ];

  if (agency?.headerText) {
    agencyInfo.push({
      text: agency.headerText,
      style: 'headerDetails',
      margin: [0, 0, 0, 4] as Margin,
    });
  }
  if (agency?.phoneNumber) {
    agencyInfo.push({
      text: `Phone: ${formatPhoneNumber(agency.phoneNumber)}`,
      style: 'headerDetails',
    });
  }
  if (agency?.email) {
    agencyInfo.push({
      text: `Email: ${agency.email}`,
      style: 'headerDetails',
    });
  }
  if (agency?.address) {
    agencyInfo.push({
      text: formatAgencyAddress(agency),
      style: 'headerDetails',
    });
  }

  const buildLogoCol = () => ({
    stack: buildLogoColumn(),
    width: 'auto' as const,
    margin: [0, 5, 0, 5] as Margin,
  });
  const agencyCol = {
    stack: agencyInfo,
    width: '*' as const,
    margin: [10, 5, 10, 5] as Margin,
  };
  const reportCol = {
    stack: reportEntries,
    width: 150 as const,
    alignment: 'left' as const,
    margin: [0, 5, 0, 5] as Margin,
  };

  let columns: Content[];
  if (alignment === 'right') {
    // Report entries far-left; the '*' spacer pushes the agency block to the right,
    // adjacent to the logo. `agencyCol` keeps its default (left) alignment so the
    // contact lines share a left edge; `width: 'auto'` lets the spacer do the pushing.
    // (Declared as consts, not inline literals, so the extra `width` key does not trip
    // excess-property checks against the `Content` union.)
    const spacerCol = { text: '', width: '*' as const };
    const agencyColRight = { ...agencyCol, width: 'auto' as const };
    columns = [reportCol, spacerCol, agencyColRight, buildLogoCol()];
  } else if (alignment === 'center') {
    // Two DISTINCT buildLogoCol() calls — pdfMake mutates nodes, so the logos must not alias.
    columns = [buildLogoCol(), agencyCol, reportCol, buildLogoCol()];
  } else {
    columns = [buildLogoCol(), agencyCol, reportCol];
  }

  return {
    table: {
      widths: ['*'],
      body: [
        [
          {
            columns,
            margin: [8, 8, 8, 8] as Margin,
            fillColor: '#E7EAEC',
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => '#000',
      vLineColor: () => '#000',
      paddingLeft: () => innerLayoutPadding.h,
      paddingRight: () => innerLayoutPadding.h,
      paddingTop: () => innerLayoutPadding.v,
      paddingBottom: () => innerLayoutPadding.v,
    },
    margin: outerMargin,
  } as Content;
};
